/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  purchaseOrderEdit,
  PURCHASE_ORDERS_ADD,
  billAddFromPO,
} from "app/routes/constant";
import ReceiptIcon from "@mui/icons-material/Receipt";
import Span from "@jumbo/shared/Span";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { deletePo, getPoList } from "app/redux/slice/poSlice";
import { getVendorList } from "app/redux/slice/vendorSlice";

const PurchaseOrder = () => {
  const navigate = useNavigate();

  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const purchaseOrderData = useSelector((state) => state?.po);
  const supplierData = useSelector((state) => state?.vendor);

  const getStoredValues = () => {
    try {
      const stored = localStorage.getItem("purchase-order-filters");
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          searchText: parsed.searchText || "",
          supplierId: parsed.supplierId || null,
          fromDate: parsed.fromDate || "",
          toDate: parsed.toDate || "",
          isInvoiceCreated: parsed.isInvoiceCreated || "pending",
          type: parsed.type || "All",
        };
      }
    } catch (error) {
      console.error("Error parsing stored filters:", error);
    }
    return {
      searchText: "",
      fromDate: "",
      toDate: "",
      supplierId: null,
      isInvoiceCreated: "pending",
      type: "All",
    };
  };

  const [searchText, setSearchText] = useState(getStoredValues().searchText);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [supplierId, setSupplierId] = useState(getStoredValues().supplierId);
  const [isInvoiceCreated, setIsInvoiceCreated] = useState(
    getStoredValues().isInvoiceCreated
  );
  const [type, setType] = useState(getStoredValues().type);
  const [fromDate, setFromDate] = useState(getStoredValues().fromDate || "");
  const [toDate, setToDate] = useState(getStoredValues().toDate || "");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const {
    isOpen: isGenerateInvoice,
    onClose: closeGenerateInvoice,
    actionProps: generateInvoiceActionProps,
    stageAction: generateInvoiceStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "purchaseOrder" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
    generateBillPermission: false,
  });

  const saveFiltersToStorage = () => {
    const filters = {
      searchText,
      supplierId,
      isInvoiceCreated,
      type,
      fromDate,
      toDate,
    };
    localStorage.setItem("purchase-order-filters", JSON.stringify(filters));
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
        generateBillPermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
        generateBillPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-purchase-order") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-purchase-order") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-purchase-order") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "create-bill") {
            permissionValue.generateBillPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    { id: "createFor", label: "Type", sort: true, minWidth: 90 },
    { id: "poNo", label: "PO No.", sort: true, minWidth: 90 },
    {
      id: "date",
      label: "Date",
      sort: true,
      minWidth: 100,
      render: (row) => moment(row?.date).format("DD-MM-YYYY"),
    },
    {
      id: "vendor",
      label: "Vendor",
      sort: true,
      render: (row) => row?.vendorId?.name,
    },
    {
      id: "totalAmount",
      label: "Total Amount",
      sort: true,
      minWidth: 100,
      isTotal: true,
      format: (value) => value?.toFixed(2),
    },
    {
      id: "remark",
      label: "Remark",
      render: (row) => {
        const remark = row?.remark || "";
        return remark.length > 15 ? remark.substring(0, 15) + "..." : remark;
      },
    },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (row) =>
        row?.status === "completed" ? (
          <Span style={{ color: "green" }}>{row?.status}</Span>
        ) : (
          <Span style={{ color: "#d77700" }}>{row?.status}</Span>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 140,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
              navigate(purchaseOrderEdit(row?._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={
              !permission.generateBillPermission || row?.status === "completed"
            }
            onClick={() => {
              generateInvoiceStageAction(row);
            }}
          >
            <ReceiptIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];

  const DeletePurchaseOrder = () => {
    closeDelete();
    dispatch(deletePo(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const GenerateInvoice = () => {
    closeGenerateInvoice();
    navigate(billAddFromPO(generateInvoiceActionProps?._id));
  };

  const getData = () => {
    // Save current filters to localStorage
    saveFiltersToStorage();

    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(supplierId && { vendorId: supplierId._id }),
      ...(isInvoiceCreated !== "All" && {
        status: isInvoiceCreated,
      }),
      ...(type !== "All" && {
        createFor: type,
      }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getPoList(value));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  // Handle all filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case "search":
        setSearchText(value);
        break;
      case "supplier":
        setSupplierId(value);
        break;
      case "type":
        setType(value);
        break;
      case "invoiceStatus":
        setIsInvoiceCreated(value);
        break;
      case "fromDate":
        setFromDate(value);
        break;
      case "toDate":
        setToDate(value);
        break;
      default:
        break;
    }
  };

  // Handle submit button click
  const handleSubmit = () => {
    handleChangePage(0, 0);
    getData();
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchText("");
  };

  useEffect(() => {
    getPermission();
    dispatch(getVendorList({ sort: 1, field: "name" }));
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {purchaseOrderData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 1, md: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={handleClearSearch}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"From Date"}
                      name="fromDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={fromDate}
                      onChange={(evt) =>
                        handleFilterChange("fromDate", evt.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"To Date"}
                      name="toDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={toDate}
                      onChange={(evt) =>
                        handleFilterChange("toDate", evt.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <JumboAutoComplete
                      options={supplierData?.list || []}
                      label="Vendor"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`supplierId`}
                      value={supplierId}
                      onChange={(evt, value) =>
                        handleFilterChange("supplier", value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        name="type"
                        id="type"
                        value={type}
                        label="type"
                        size="small"
                        onChange={(e) =>
                          handleFilterChange("type", e.target.value)
                        }
                      >
                        <MenuItem value="All">All</MenuItem>
                        {[
                          "Fabric",
                          "Stitch",
                          "Modeling",
                          "ReadyMade",
                          "Dyer",
                        ].map((val) => {
                          return (
                            <MenuItem key={val} value={val}>
                              {val}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Invoice Generate Status</InputLabel>
                      <Select
                        name="isInvoiceCreated"
                        id="isInvoiceCreated"
                        value={isInvoiceCreated}
                        label="Invoice Generate Status"
                        size="small"
                        onChange={(e) =>
                          handleFilterChange("invoiceStatus", e.target.value)
                        }
                      >
                        {["All", "pending", "completed"].map((val) => {
                          return (
                            <MenuItem key={val} value={val}>
                              {val}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Purchase Order List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate(PURCHASE_ORDERS_ADD);
                    }}
                    sx={{
                      mt: { xs: 1, md: 0 },
                    }}
                    type="submit"
                    variant="contained"
                  >
                    Add Purchase Order
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={purchaseOrderData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={purchaseOrderData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
                showTotalInFooter={true}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isGenerateInvoice}
            onClose={closeGenerateInvoice}
            title="Are you sure you want to generate bill?"
            onConfirm={GenerateInvoice}
            btnLabels={{ cancel: "Cancel", confirm: "Generate" }}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the purchase order?"
            onConfirm={DeletePurchaseOrder}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default PurchaseOrder;
